import { LINKEDIN_AUTOMATION_STAGE_STATUSES } from './linkedinAutomationStageStatuses'
import { emailVisibilityUrl } from './linkedinUrls'
import {
  getCurrentValueForRadioButtons,
  saveCurrentValueForRadioButtons,
  toggleRadioButton,
} from './radioButtonUtils'
import { LinkedinSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { LinkedinRecommendationKey, StageType } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import {
  getCurrentValueForCheckbox,
  saveCurrentValueForCheckbox,
  toggleCheckbox,
} from '../checkboxDetection'
import {
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

const EMAIL_VISIBILITY_SETTINGS = {
  ONLY_ME: 'Only visible to me',
  FIRST_DEGREE: '1st degree connections',
  SECOND_DEGREE: '1st and 2nd degree connections',
  ANYONE: 'Anyone on LinkedIn',
}

const EMAIL_EXPORT_SETTINGS = {
  EMAIL_EXPORT: 'Allow connection to export emails',
}

// Settings property function
const currentValueFns = {
  [LinkedinSettingKey.EMAIL_VISIBILITY]: getCurrentValueFnFromKey(
    LinkedinSettingKey.EMAIL_VISIBILITY,
    (value) => value,
  ),
  [LinkedinSettingKey.EMAIL_EXPORT]: getCurrentValueFnFromKey(
    LinkedinSettingKey.EMAIL_EXPORT,
    (value) => (value ? 'On' : 'Off'),
  ),
}

const fulfilledFns = {
  [LinkedinSettingKey.EMAIL_VISIBILITY]: getIsFulfilledFnFromKey(
    LinkedinSettingKey.EMAIL_VISIBILITY,
    (emailVisibility) =>
      emailVisibility === EMAIL_VISIBILITY_SETTINGS.ONLY_ME ||
      emailVisibility === EMAIL_VISIBILITY_SETTINGS.FIRST_DEGREE,
  ),
  [LinkedinSettingKey.EMAIL_EXPORT]: getIsFulfilledFnFromKey(
    LinkedinSettingKey.EMAIL_EXPORT,
    (value) => !value,
  ),
}

const getCurrentValueFn = (key) => currentValueFns[key]
const getIsFulfilledFn = (key) => fulfilledFns[key]
const getCantUndoFn = () => defaultCantUndoFn

function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

// Automations

enum StageKey {
  LINKEDIN_EMAIL_VISIBILITY_GATHER = 'LINKEDIN_EMAIL_VISIBILITY_GATHER',
  LINKEDIN_EMAIL_EXPORT_GATHER = 'LINKEDIN_EMAIL_EXPORT_GATHER',
  LINKEDIN_EMAIL_VISIBILITY_UPDATE = 'LINKEDIN_EMAIL_VISIBILITY_UPDATE',
  LINKEDIN_EMAIL_EXPORT_UPDATE = 'LINKEDIN_EMAIL_EXPORT_UPDATE',
  LINKEDIN_EMAIL_VISIBILITY_UNDO = 'LINKEDIN_EMAIL_VISIBILITY_UNDO',
  LINKEDIN_EMAIL_EXPORT_UNDO = 'LINKEDIN_EMAIL_EXPORT_UNDO',
}

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_EMAIL_VISIBILITY_GATHER,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: emailVisibilityUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForRadioButtons(
        userContext.updateStoredInitialValues,
        EMAIL_VISIBILITY_SETTINGS,
        LinkedinSettingKey.EMAIL_VISIBILITY,
        LinkedinRecommendationKey.LINKEDIN_EMAIL_VISIBILITY,
      )
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_EMAIL_EXPORT_GATHER,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: emailVisibilityUrl,
    action: async (machineContext, userContext) => {
      await saveCurrentValueForCheckbox(
        userContext.updateStoredAutomationErrors,
        userContext.updateStoredInitialValues,
        EMAIL_EXPORT_SETTINGS,
        LinkedinSettingKey.EMAIL_EXPORT,
        LinkedinRecommendationKey.LINKEDIN_EMAIL_VISIBILITY,
        'p',
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_EMAIL_VISIBILITY_UPDATE,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: emailVisibilityUrl,
    action: async (machineContext, userContext) => {
      const { isDry } = userContext

      const currentSetting = await getCurrentValueForRadioButtons(
        EMAIL_VISIBILITY_SETTINGS,
      )

      if (
        !(
          currentSetting === EMAIL_VISIBILITY_SETTINGS.ONLY_ME ||
          currentSetting === EMAIL_VISIBILITY_SETTINGS.FIRST_DEGREE
        ) &&
        !isDry
      ) {
        return toggleRadioButton(EMAIL_VISIBILITY_SETTINGS.FIRST_DEGREE)
      }
      return null
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_EMAIL_EXPORT_UPDATE,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: emailVisibilityUrl,
    action: async (machineContext, userContext) => {
      const { isDry } = userContext

      const currentSetting = await getCurrentValueForCheckbox(
        EMAIL_EXPORT_SETTINGS.EMAIL_EXPORT,
        'p',
      )

      if (currentSetting && !isDry) {
        return toggleCheckbox(EMAIL_EXPORT_SETTINGS.EMAIL_EXPORT, 'p')
      }
      return null
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_EMAIL_VISIBILITY_UNDO,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: emailVisibilityUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry } = userContext

      const currentSetting = getCurrentValueForRadioButtons(
        EMAIL_VISIBILITY_SETTINGS,
      )

      // Toggle setting if checkbox does not match initial values
      if (
        initialValues?.[LinkedinSettingKey.EMAIL_VISIBILITY] !==
          currentSetting &&
        !isDry
      ) {
        return toggleRadioButton(
          initialValues[LinkedinSettingKey.EMAIL_VISIBILITY],
        )
      }
      return null
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.LINKEDIN_EMAIL_EXPORT_UNDO,
    status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: emailVisibilityUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry } = userContext

      const currentSetting = await getCurrentValueForCheckbox(
        EMAIL_EXPORT_SETTINGS.EMAIL_EXPORT,
        'p',
      )

      // Toggle setting if checkbox does not match initial values
      if (
        initialValues?.[LinkedinSettingKey.EMAIL_EXPORT] !== currentSetting &&
        !isDry
      ) {
        return toggleCheckbox(EMAIL_EXPORT_SETTINGS.EMAIL_EXPORT, 'p')
      }
      return null
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
